import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AGENTS } from '../lib/agents';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/auth.store';

// ----- Types -----

interface Session {
  id: string;
  agentId: string;
  journeyId: string | null;
  title: string;
  date: string;
  status: 'Open' | 'Closed';
}

interface Journey {
  id: string;
  name: string;
  desiredOutcome: string;
  status: 'Active' | 'Paused' | 'Completed' | 'Archived';
}

interface Insight {
  id: string;
  agentId: string;
  sourceExcerpt: string;
  date: string;
  tags: string[];
}

// ----- API response types -----

interface ApiSession {
  id: string;
  agentId: string;
  agentName: string;
  journeyId: string | null;
  title: string;
  status: string;
  startedAt: string;
  closedAt: string | null;
  lastMessageAt: string | null;
}

interface ApiJourney {
  id: string;
  name: string;
  desiredOutcome: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface ApiInsight {
  id: string;
  userId: string;
  sessionId: string | null;
  agentId: string | null;
  agentName: string | null;
  sourceText: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

// ----- Main Page -----

// ----- Data fetching helpers -----

function formatDateShort(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function mapApiSessions(apiSessions: ApiSession[]): Session[] {
  return apiSessions.map((s) => ({
    id: s.id,
    agentId: s.agentId,
    journeyId: s.journeyId,
    title: s.title,
    date: formatDateShort(s.startedAt),
    status: s.status === 'Open' ? 'Open' : 'Closed',
  }));
}

function mapApiJourneys(apiJourneys: ApiJourney[]): Journey[] {
  return apiJourneys.map((j) => ({
    id: j.id,
    name: j.name,
    desiredOutcome: j.desiredOutcome,
    status: j.status as Journey['status'],
  }));
}

function mapApiInsights(apiInsights: ApiInsight[]): Insight[] {
  return apiInsights.map((i) => {
    // Resolve the agent slug from the agent name returned by the API
    const matchedAgent = i.agentName
      ? AGENTS.find((a) => a.name.toLowerCase() === i.agentName!.toLowerCase())
      : undefined;
    return {
      id: i.id,
      agentId: matchedAgent?.id ?? '',
      sourceExcerpt: i.sourceText,
      date: formatDateShort(i.createdAt),
      tags: i.tags,
    };
  });
}

/**
 * WorkspacePage — personal dashboard with sessions, journeys, and insights.
 * Responsive: tabs on mobile, side-by-side panels on desktop.
 */
export function WorkspacePage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<'sessions' | 'journeys' | 'insights'>('sessions');
  const [sessions, setSessions] = useState<Session[]>([]);
  const [journeys, setJourneys] = useState<Journey[]>([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch data from real API on mount
  useEffect(() => {
    async function fetchDashboardData() {
      try {
        const [apiSessions, apiJourneys, apiInsights] = await Promise.all([
          api.get<ApiSession[]>('/sessions'),
          api.get<ApiJourney[]>('/journeys'),
          api.get<ApiInsight[]>('/insights'),
        ]);
        setSessions(mapApiSessions(apiSessions));
        setJourneys(mapApiJourneys(apiJourneys));
        setInsights(mapApiInsights(apiInsights));
      } catch {
        // Silently fail — panels will show empty state
      } finally {
        setLoading(false);
      }
    }
    fetchDashboardData();
  }, []);

  // Dashboard summary stats
  const sessionCount = sessions.length;
  const insightCount = insights.length;
  const actionCount = insights.filter((i) => i.tags.includes('action')).length;
  const lastOpenSession = sessions.find((s) => s.status === 'Open');

  return (
    <div className="px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-text-primary sm:text-3xl">My Dashboard</h1>
          <p className="mt-1 text-text-secondary">
            Your sessions, journeys, and insights — all in one place.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <p className="text-sm text-text-muted">Loading your dashboard…</p>
          </div>
        ) : (
        <>
        {/* Dashboard summary cards */}
        <div className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Sessions started" value={sessionCount} />
          <StatCard label="Insights captured" value={insightCount} />
          <StatCard label="Actions identified" value={actionCount} />
          {lastOpenSession ? (
            <Link
              to={`/session/${lastOpenSession.id}`}
              className="flex flex-col justify-center rounded-lg border border-border bg-surface-raised p-4 transition hover:shadow-sm"
            >
              <span className="text-xs font-medium uppercase text-text-muted">Continue</span>
              <span className="mt-1 truncate text-sm font-medium text-byron">
                {lastOpenSession.title}
              </span>
            </Link>
          ) : (
            <div className="flex flex-col justify-center rounded-lg border border-border bg-surface-raised p-4">
              <span className="text-xs font-medium uppercase text-text-muted">Continue</span>
              <span className="mt-1 text-sm text-text-muted">No open sessions</span>
            </div>
          )}
        </div>

        {/* Mobile tab bar */}
        <div className="mb-6 flex gap-1 rounded-lg bg-surface-muted p-1 lg:hidden">
          <TabButton active={activeTab === 'sessions'} onClick={() => setActiveTab('sessions')}>
            Sessions
          </TabButton>
          <TabButton active={activeTab === 'journeys'} onClick={() => setActiveTab('journeys')}>
            Journeys
          </TabButton>
          <TabButton active={activeTab === 'insights'} onClick={() => setActiveTab('insights')}>
            Insights & Actions
          </TabButton>
        </div>

        {/* Desktop: three-panel side-by-side / Mobile: active tab content */}
        <div className="grid gap-6 lg:grid-cols-3">
          <div className={activeTab === 'sessions' ? '' : 'hidden lg:block'}>
            <SessionsPanel sessions={sessions} journeys={journeys} onDeleted={(id) => setSessions((prev) => prev.filter((s) => s.id !== id))} onRenamed={(id, title) => setSessions((prev) => prev.map((s) => s.id === id ? { ...s, title } : s))} onJourneyChanged={(id, journeyId) => setSessions((prev) => prev.map((s) => s.id === id ? { ...s, journeyId } : s))} />
          </div>
          <div className={activeTab === 'journeys' ? '' : 'hidden lg:block'}>
            <JourneysPanel journeys={journeys} onCreated={(j) => setJourneys((prev) => [j, ...prev])} onStatusChanged={(id, status) => setJourneys((prev) => prev.map((j) => j.id === id ? { ...j, status } : j))} onUpdated={(id, name, desiredOutcome) => setJourneys((prev) => prev.map((j) => j.id === id ? { ...j, name, desiredOutcome } : j))} />
          </div>
          <div className={activeTab === 'insights' ? '' : 'hidden lg:block'}>
            <InsightsPanel insights={insights} onDeleted={(id) => setInsights((prev) => prev.filter((i) => i.id !== id))} />
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ----- Panels -----

function SessionsPanel({ sessions, journeys, onDeleted, onRenamed, onJourneyChanged }: { sessions: Session[]; journeys: Journey[]; onDeleted: (id: string) => void; onRenamed: (id: string, title: string) => void; onJourneyChanged: (id: string, journeyId: string | null) => void }) {
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  if (sessions.length === 0) {
    return (
      <PanelShell title="Sessions">
        <EmptyState message="Start a session with any agent to see it here." />
      </PanelShell>
    );
  }

  async function handleRenameSubmit(sessionId: string) {
    const trimmed = renameValue.trim();
    if (!trimmed) {
      setRenameTarget(null);
      return;
    }
    try {
      await api.patch(`/sessions/${sessionId}`, { title: trimmed });
      onRenamed(sessionId, trimmed);
    } catch {
      // silently fail
    }
    setRenameTarget(null);
  }

  async function handleExport(sessionId: string, format: string) {
    try {
      const token = useAuthStore.getState().token;
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';
      const url = `${baseUrl}/sessions/${sessionId}/export?format=${format.toLowerCase()}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `session-${sessionId}.${format.toLowerCase() === 'pdf' ? 'pdf' : 'txt'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch {
      // silently fail
    }
  }

  return (
    <PanelShell title="Sessions">
      <ul className="flex flex-col gap-2">
        {sessions.map((session) => {
          const agent = AGENTS.find((a) => a.id === session.agentId);
          const isRenaming = renameTarget === session.id;
          return (
            <li
              key={session.id}
              className="flex items-center gap-3 rounded border border-border bg-surface p-3"
            >
              {/* Agent colour dot */}
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full"
                style={{ backgroundColor: agent?.colour ?? '#999' }}
                aria-label={agent?.name ?? 'Agent'}
              />
              {isRenaming ? (
                <form
                  className="flex-1"
                  onSubmit={(e) => { e.preventDefault(); handleRenameSubmit(session.id); }}
                >
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={() => handleRenameSubmit(session.id)}
                    autoFocus
                    className="w-full rounded border border-border bg-surface-raised px-2 py-1 text-sm text-text-primary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                    maxLength={200}
                  />
                </form>
              ) : (
                <Link to={`/session/${session.id}`} className="flex-1 truncate text-sm text-text-primary hover:underline">
                  {session.title}
                </Link>
              )}
              <span className="text-xs text-text-muted">{session.date}</span>
              <StatusBadge status={session.status} />
              {/* Actions */}
              <button
                aria-label="Rename session"
                className="text-text-muted hover:text-text-secondary"
                title="Rename"
                onClick={() => {
                  setRenameTarget(session.id);
                  setRenameValue(session.title);
                }}
              >
                <PencilIcon />
              </button>
              <button
                aria-label="Delete session"
                className="text-text-muted hover:text-agent-red"
                title="Delete"
                onClick={() => setDeleteTarget(session.id)}
              >
                <TrashIcon />
              </button>
              <ExportDropdown onExport={(fmt) => handleExport(session.id, fmt)} />
              {/* Journey assignment */}
              <select
                value={session.journeyId ?? ''}
                onChange={async (e) => {
                  const newJourneyId = e.target.value || null;
                  try {
                    await api.patch(`/sessions/${session.id}`, { journeyId: newJourneyId });
                    onJourneyChanged(session.id, newJourneyId);
                  } catch {
                    // silently fail
                  }
                }}
                className="max-w-[90px] rounded border border-border bg-surface-raised px-1 py-0.5 text-[10px] text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                aria-label="Assign journey"
                title="Assign to Journey"
              >
                <option value="">No Journey</option>
                {journeys.filter((j) => j.status === 'Active' || j.id === session.journeyId).map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
            </li>
          );
        })}
      </ul>

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <ConfirmModal
          message="Delete this session? This action cannot be undone."
          onConfirm={async () => {
            try {
              await api.delete(`/sessions/${deleteTarget}`, { confirmed: true });
              onDeleted(deleteTarget);
            } catch {
              // silently fail
            }
            setDeleteTarget(null);
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </PanelShell>
  );
}

function JourneysPanel({ journeys, onCreated, onStatusChanged, onUpdated }: { journeys: Journey[]; onCreated: (j: Journey) => void; onStatusChanged: (id: string, status: Journey['status']) => void; onUpdated: (id: string, name: string, desiredOutcome: string) => void }) {
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newOutcome, setNewOutcome] = useState('');
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editOutcome, setEditOutcome] = useState('');

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    try {
      const created = await api.post<ApiJourney>('/journeys', {
        name: newName,
        desiredOutcome: newOutcome,
      });
      onCreated({
        id: created.id,
        name: created.name,
        desiredOutcome: created.desiredOutcome,
        status: created.status as Journey['status'],
      });
    } catch {
      // silently fail
    }
    setShowCreate(false);
    setNewName('');
    setNewOutcome('');
  }

  async function handleEditSave(journeyId: string) {
    const trimmedName = editName.trim();
    const trimmedOutcome = editOutcome.trim();
    if (!trimmedName || !trimmedOutcome) {
      setEditTarget(null);
      return;
    }
    try {
      await api.patch(`/journeys/${journeyId}`, { name: trimmedName, desiredOutcome: trimmedOutcome });
      onUpdated(journeyId, trimmedName, trimmedOutcome);
    } catch {
      // silently fail
    }
    setEditTarget(null);
  }

  return (
    <PanelShell
      title="Journeys"
      action={
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="rounded bg-gail px-3 py-1 text-xs font-medium text-white hover:bg-gail-accent"
        >
          {showCreate ? 'Cancel' : 'Create Journey'}
        </button>
      }
    >
      {showCreate && (
        <form onSubmit={handleCreate} className="mb-4 flex flex-col gap-2 rounded border border-border bg-surface p-3">
          <input
            type="text"
            placeholder="Journey name"
            required
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="rounded border border-border bg-surface-raised px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
          />
          <input
            type="text"
            placeholder="Desired outcome"
            required
            value={newOutcome}
            onChange={(e) => setNewOutcome(e.target.value)}
            className="rounded border border-border bg-surface-raised px-2 py-1.5 text-sm text-text-primary placeholder:text-text-muted focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
          />
          <button
            type="submit"
            className="self-end rounded bg-gail px-3 py-1.5 text-xs font-medium text-white hover:bg-gail-accent"
          >
            Save
          </button>
        </form>
      )}

      {journeys.length === 0 && !showCreate ? (
        <EmptyState message="Create a Journey to track related sessions and insights over time." />
      ) : (
        <ul className="flex flex-col gap-2">
          {journeys.map((journey) => {
            const isEditing = editTarget === journey.id;
            return (
              <li
                key={journey.id}
                className="flex items-center gap-3 rounded border border-border bg-surface p-3"
              >
                {isEditing ? (
                  <form
                    className="flex-1 flex flex-col gap-1.5"
                    onSubmit={(e) => { e.preventDefault(); handleEditSave(journey.id); }}
                  >
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      autoFocus
                      className="w-full rounded border border-border bg-surface-raised px-2 py-1 text-sm text-text-primary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                    />
                    <input
                      type="text"
                      value={editOutcome}
                      onChange={(e) => setEditOutcome(e.target.value)}
                      className="w-full rounded border border-border bg-surface-raised px-2 py-1 text-xs text-text-primary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
                    />
                    <div className="flex gap-2 self-end">
                      <button type="button" onClick={() => setEditTarget(null)} className="text-xs text-text-muted hover:text-text-secondary">Cancel</button>
                      <button type="submit" className="text-xs font-medium text-gail hover:text-gail-accent">Save</button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary">{journey.name}</p>
                      <p className="truncate text-xs text-text-muted">{journey.desiredOutcome}</p>
                    </div>
                    <button
                      aria-label="Edit journey"
                      className="text-text-muted hover:text-text-secondary"
                      title="Edit"
                      onClick={() => {
                        setEditTarget(journey.id);
                        setEditName(journey.name);
                        setEditOutcome(journey.desiredOutcome);
                      }}
                    >
                      <PencilIcon />
                    </button>
                  </>
                )}
                <JourneyStatusDropdown status={journey.status} onChange={async (newStatus) => {
                  try {
                    await api.patch<ApiJourney>(`/journeys/${journey.id}`, { status: newStatus });
                    onStatusChanged(journey.id, newStatus);
                  } catch {
                    // silently fail
                  }
                }} />
              </li>
            );
          })}
        </ul>
      )}
    </PanelShell>
  );
}

function InsightsPanel({ insights, onDeleted }: { insights: Insight[]; onDeleted: (id: string) => void }) {
  const [search, setSearch] = useState('');
  const [filterAgent, setFilterAgent] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'insight' | 'action'>('all');

  const filtered = insights.filter((insight) => {
    const matchesSearch =
      !search || insight.sourceExcerpt.toLowerCase().includes(search.toLowerCase());
    const matchesAgent = !filterAgent || insight.agentId === filterAgent;
    const matchesType =
      filterType === 'all' ||
      (filterType === 'action' && insight.tags.includes('action')) ||
      (filterType === 'insight' && !insight.tags.includes('action'));
    return matchesSearch && matchesAgent && matchesType;
  });

  return (
    <PanelShell title="Insights & Actions" action={<ExportDropdown formats={['CSV', 'JSON']} onExport={async (fmt) => {
      try {
        const token = useAuthStore.getState().token;
        const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api';
        const url = `${baseUrl}/insights/export?format=${fmt.toLowerCase()}`;
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `insights-export.${fmt.toLowerCase() === 'csv' ? 'csv' : 'json'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      } catch {
        // silently fail
      }
    }} />}>
      {/* Search */}
      <input
        type="search"
        placeholder="Search insights & actions…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3 w-full rounded border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-byron focus:outline-none focus:ring-1 focus:ring-byron"
        aria-label="Search insights and actions"
      />

      {/* Type filter chips */}
      <div className="mb-2 flex gap-1.5">
        <button
          onClick={() => setFilterType('all')}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
            filterType === 'all' ? 'bg-black text-white' : 'bg-surface-muted text-text-secondary hover:bg-border'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilterType('insight')}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
            filterType === 'insight' ? 'bg-byron text-white' : 'bg-surface-muted text-text-secondary hover:bg-border'
          }`}
        >
          💡 Insights
        </button>
        <button
          onClick={() => setFilterType('action')}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
            filterType === 'action' ? 'bg-gail text-white' : 'bg-surface-muted text-text-secondary hover:bg-border'
          }`}
        >
          ✅ Actions
        </button>
      </div>

      {/* Agent filter chips */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        <button
          onClick={() => setFilterAgent(null)}
          className={`rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
            !filterAgent
              ? 'bg-black text-white'
              : 'bg-surface-muted text-text-secondary hover:bg-border'
          }`}
        >
          All
        </button>
        {AGENTS.map((agent) => (
          <button
            key={agent.id}
            onClick={() => setFilterAgent(agent.id)}
            className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium transition ${
              filterAgent === agent.id
                ? 'text-white'
                : 'bg-surface-muted text-text-secondary hover:bg-border'
            }`}
            style={filterAgent === agent.id ? { backgroundColor: agent.colour } : undefined}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: agent.colour }}
              aria-hidden="true"
            />
            {agent.name}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState message="Hover over any agent response to save it as an Insight 💡 or Action ✅ on your Dashboard." />
      ) : (
        <ul className="flex flex-col gap-2">
          {filtered.map((insight) => {
            const agent = AGENTS.find((a) => a.id === insight.agentId);
            return (
              <li
                key={insight.id}
                className="rounded border border-border bg-surface p-3"
              >
                <div className="mb-1 flex items-start gap-2">
                  <span className="mt-0.5 shrink-0 text-xs">
                    {insight.tags.includes('action') ? '✅' : '💡'}
                  </span>
                  <p className="text-sm text-text-primary line-clamp-3">
                    {insight.sourceExcerpt}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-text-muted">
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: agent?.colour ?? '#999' }}
                    aria-hidden="true"
                  />
                  <span>{agent?.name ?? 'Unknown'}</span>
                  <span>•</span>
                  <span>{insight.date}</span>
                  <span>•</span>
                  <span className={insight.tags.includes('action') ? 'text-gail font-medium' : 'text-byron font-medium'}>
                    {insight.tags.includes('action') ? 'Action' : 'Insight'}
                  </span>
                </div>
                {/* Delete action */}
                <div className="mt-2 flex gap-2">
                  <button
                    className="text-xs text-text-muted hover:text-agent-red"
                    aria-label="Delete insight"
                    onClick={async () => {
                      try {
                        await api.delete(`/insights/${insight.id}`, { confirmed: true });
                        onDeleted(insight.id);
                      } catch {
                        // silently fail
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </PanelShell>
  );
}

// ----- Shared UI Components -----

function PanelShell({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-surface-raised p-4">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function StatCard({ label, value, note }: { label: string; value: number; note?: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface-raised p-4">
      <span className="text-xs font-medium uppercase text-text-muted">{label}</span>
      <p className="mt-1 text-2xl font-bold text-text-primary">{value}</p>
      {note && <p className="mt-1 text-xs text-text-muted italic">🚧 {note}</p>}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
        active
          ? 'bg-surface-raised text-text-primary shadow-sm'
          : 'text-text-secondary hover:text-text-primary'
      }`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const isOpen = status === 'Open' || status === 'Active';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
        isOpen ? 'bg-gail-light text-gail' : 'bg-surface-muted text-text-muted'
      }`}
    >
      {status}
    </span>
  );
}

function JourneyStatusDropdown({
  status,
  onChange,
}: {
  status: Journey['status'];
  onChange: (newStatus: Journey['status']) => void;
}) {
  return (
    <select
      value={status}
      onChange={(e) => onChange(e.target.value as Journey['status'])}
      className="rounded border border-border bg-surface-raised px-2 py-1 text-xs text-text-secondary focus:border-gail focus:outline-none focus:ring-1 focus:ring-gail"
      aria-label="Change journey status"
    >
      <option value="Active">Active</option>
      <option value="Paused">Paused</option>
      <option value="Completed">Completed</option>
    </select>
  );
}

function ExportDropdown({ formats = ['TXT', 'PDF'], onExport }: { formats?: string[]; onExport?: (format: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs text-text-muted hover:text-text-secondary"
        aria-label="Export"
        title="Export"
      >
        <DownloadIcon />
      </button>
      {open && (
        <div className="absolute right-0 top-6 z-10 rounded border border-border bg-surface-raised py-1 shadow-lg">
          {formats.map((fmt) => (
            <button
              key={fmt}
              onClick={() => {
                onExport?.(fmt);
                setOpen(false);
              }}
              className="block w-full px-4 py-1.5 text-left text-xs text-text-secondary hover:bg-surface-muted"
            >
              {fmt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ConfirmModal({
  message,
  onConfirm,
  onCancel,
}: {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="mx-4 w-full max-w-sm rounded-lg border border-border bg-surface-raised p-6 shadow-xl">
        <p className="mb-4 text-sm text-text-primary">{message}</p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded border border-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-muted"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded bg-agent-red px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded border border-dashed border-border p-6 text-center">
      <p className="text-sm text-text-muted">{message}</p>
    </div>
  );
}

// ----- Icons -----

function PencilIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}
